import { Decimal } from 'decimal.js';
import { roundRate, formatRateForDisplay, isValidRateInput as isValidRateInputUtil, recalculateItemPrice } from './salePriceRuleUtils';
import { getItemByIdAsync, calculateItemPriceAsync } from '@/lib/indexeddb/database';
import { getItemPriceDetailsWithFallback, isSQLiteAvailable } from '@/lib/indexeddb/integrationUtils';
import { getFinalItemPriceDetails } from './salePriceRuleUtils';

// Re-export for convenience
export { isValidRateInput } from './salePriceRuleUtils';

// Types
export interface InvoiceLineItem {
  item_id?: number | string;
  item_name?: string;
  item_code?: string;
  description?: string;
  quantity?: number | string;
  rate?: number | string;
  rate_decimal_places?: number;
  discount_percent?: number | string;
  discount_amount?: number | string;
  tax_rate_id?: number | string | null;
  tax_rate_percent?: number;
  is_tax_inclusive?: boolean;
  taxable_amount?: number;
  tax_amount?: number;
  amount?: number;
  godown_id?: number | string;
  applied_sale_rule_id?: number | null;
}

export interface ItemPricingResult {
  item_id: number;
  item_name: string;
  item_code: string;
  description: string;
  rate: number;
  rate_decimal_places: number;
  discount_percent: number;
  discount_amount: number;
  tax_rate_id: number | null;
  tax_rate_percent: number;
  is_tax_inclusive: boolean;
  taxable_amount: number;
  tax_amount: number;
  amount: number;
  applied_sale_rule_id: number | null;
  tax_breakdown: Array<{ code: string; rate: number; amount: number }>;
}

export interface CalculatedTotals {
  taxable_amount: number;
  tax_amount: number;
  amount: number;
}

/**
 * Get item details and calculate pricing from IndexedDB first, with API fallback
 */
export async function getItemPricingDetails(
  itemId: number,
  quantity: number = 1,
  siteId: number | null = null,
  partyId: number | null = null,
  existingDiscountPercent: number = 0,
  existingDiscountAmount: number = 0
): Promise<ItemPricingResult | null> {
  try {
    console.log(`[invoiceCalculations] Getting pricing for item ${itemId}, qty: ${quantity}`);
    
    // First, get item details from IndexedDB
    let item = null;
    let rateDecimalPlaces = 0;
    
    if (isSQLiteAvailable()) {
      item = await getItemByIdAsync(itemId);
      if (item) {
        rateDecimalPlaces = item.rate_decimal_places || 0;
        console.log(`[invoiceCalculations] Found item in IndexedDB with rate_decimal_places: ${rateDecimalPlaces}`);
      }
    }
    
    // Get pricing details
    let priceDetails = null;
    
    if (isSQLiteAvailable()) {
      console.log("[invoiceCalculations] Using IndexedDB for pricing calculation");
      priceDetails = await getItemPriceDetailsWithFallback(
        itemId,
        quantity,
        siteId,
        partyId,
        undefined, // priceListId
        item?.tax_rate_id || null,
        // Fallback to API if SQLite fails
        async (id, qty) => getFinalItemPriceDetails(
          id,
          undefined,
          siteId,
          partyId,
          qty,
          item?.tax_rate_id || null,
          rateDecimalPlaces
        )
      );
    } else {
      // Use API directly if SQLite not available
      console.log("[invoiceCalculations] IndexedDB not available, using API");
      priceDetails = await getFinalItemPriceDetails(
        itemId,
        undefined,
        siteId,
        partyId,
        quantity,
        item?.tax_rate_id || null,
        rateDecimalPlaces
      );
    }
    
    if (!priceDetails) {
      console.error(`[invoiceCalculations] Failed to get price details for item ${itemId}`);
      return null;
    }
    
    // Apply proper rate rounding based on decimal places
    const roundedRate = roundRate(priceDetails.rate, rateDecimalPlaces).toNumber();
    
    // If user has custom discounts, recalculate with them
    let finalDetails = {
      rate: roundedRate,
      discount_percent: priceDetails.discount_percent.toNumber(),
      discount_amount: priceDetails.discount_amount.toNumber(),
      taxable_amount: priceDetails.taxable_amount.toNumber(),
      tax_amount: priceDetails.tax_amount.toNumber(),
      amount: priceDetails.final_amount.toNumber(),
    };
    
    if (existingDiscountPercent > 0 || existingDiscountAmount > 0) {
      // Recalculate with user's custom discounts
      const recalculated = recalculateItemPrice(
        itemId,
        priceDetails.tax_rate_id,
        quantity,
        roundedRate,
        existingDiscountPercent,
        existingDiscountAmount,
        priceDetails.is_tax_inclusive || false,
        priceDetails.tax_breakdown ? 
          priceDetails.tax_breakdown.reduce((sum, comp) => sum + comp.rate.toNumber(), 0) : 0,
        rateDecimalPlaces
      );
      
      finalDetails = {
        rate: roundedRate,
        discount_percent: existingDiscountPercent,
        discount_amount: existingDiscountAmount,
        taxable_amount: recalculated.taxableAmount,
        tax_amount: recalculated.taxAmount,
        amount: recalculated.totalAmount,
      };
    }
    
    return {
      item_id: itemId,
      item_name: item?.name || '',
      item_code: item?.code || '',
      description: item?.description || item?.name || '',
      rate: finalDetails.rate,
      rate_decimal_places: rateDecimalPlaces,
      discount_percent: finalDetails.discount_percent,
      discount_amount: finalDetails.discount_amount,
      tax_rate_id: priceDetails.tax_rate_id,
      tax_rate_percent: priceDetails.tax_breakdown ? 
        priceDetails.tax_breakdown.reduce((sum, comp) => sum + comp.rate.toNumber(), 0) : 0,
      is_tax_inclusive: priceDetails.is_tax_inclusive || false,
      taxable_amount: finalDetails.taxable_amount,
      tax_amount: finalDetails.tax_amount,
      amount: finalDetails.amount,
      applied_sale_rule_id: priceDetails.applied_sale_rule_id || null,
      tax_breakdown: priceDetails.tax_breakdown.map(comp => ({
        code: comp.code,
        rate: comp.rate.toNumber(),
        amount: comp.amount.toNumber()
      }))
    };
  } catch (error) {
    console.error(`[invoiceCalculations] Error getting pricing details for item ${itemId}:`, error);
    return null;
  }
}

/**
 * Get item details and calculate purchase pricing from IndexedDB
 * This is specifically for purchase invoices and uses purchase prices instead of sales prices
 */
export async function getPurchaseItemPricingDetails(
  itemId: number,
  quantity: number = 1,
  siteId: number | null = null,
  partyId: number | null = null,
  existingDiscountPercent: number = 0,
  existingDiscountAmount: number = 0
): Promise<ItemPricingResult | null> {
  try {
    console.log(`[invoiceCalculations] Getting purchase pricing for item ${itemId}, qty: ${quantity}`);
    
    // First, get item details from IndexedDB
    let item = null;
    let rateDecimalPlaces = 0;
    
    if (isSQLiteAvailable()) {
      item = await getItemByIdAsync(itemId);
      if (item) {
        rateDecimalPlaces = item.rate_decimal_places || 0;
        console.log(`[invoiceCalculations] Found item in IndexedDB with rate_decimal_places: ${rateDecimalPlaces}`);
      }
    }
    
    // Get purchase price from IndexedDB
    let purchasePrice = null;
    if (isSQLiteAvailable()) {
      const { getItemPriceAsync } = await import('@/lib/indexeddb/database');
      purchasePrice = await getItemPriceAsync(itemId, 'purchase', siteId);
      console.log(`[invoiceCalculations] Purchase price from IndexedDB: ${purchasePrice}`);
    }
    
    if (purchasePrice === null) {
      console.error(`[invoiceCalculations] No purchase price found for item ${itemId}`);
      return null;
    }
    
    // Apply proper rate rounding based on decimal places
    const roundedRate = roundRate(new Decimal(purchasePrice), rateDecimalPlaces).toNumber();
    
    // Use the same tax calculation logic as sales invoices
    // Get pricing details but with purchase price
    const priceDetails = await calculateItemPriceAsync(itemId, quantity, 'purchase', siteId);
    
    if (!priceDetails) {
      console.error(`[invoiceCalculations] Failed to calculate price details for item ${itemId}`);
      return null;
    }
    
    // Get the full item with tax information
    let taxRateId = item?.tax_rate_id || null;
    let taxRatePercent = 0;
    let isTaxInclusive = false;
    let taxBreakdown: Array<{ code: string; rate: number; amount: number }> = [];
    
    console.log(`[getPurchaseItemPricingDetails] Item data:`, {
      item_id: itemId,
      item_name: item?.name,
      tax_rate_id: item?.tax_rate_id,
      tax_rate: item?.tax_rate,
      is_tax_inclusive: item?.is_tax_inclusive,
      prices: item?.prices
    });
    
    // Check tax inclusive from prices object
    if (item?.prices?.is_tax_inclusive !== undefined) {
      isTaxInclusive = item.prices.is_tax_inclusive;
    }
    
    // For site-specific prices, check site-specific tax inclusive flag
    if (siteId && item?.prices?.site_prices) {
      const sitePrice = item.prices.site_prices.find((sp: any) => sp.site_id === siteId);
      if (sitePrice?.is_tax_inclusive !== undefined) {
        isTaxInclusive = sitePrice.is_tax_inclusive;
      }
    }
    
    // Check if we have tax rate information - check for tax_rate object even without tax_rate_id
    if (item?.tax_rate) {
      // Extract tax_rate_id from the tax_rate object if not directly available
      if (!taxRateId && item.tax_rate.id) {
        taxRateId = item.tax_rate.id;
      }
      taxRatePercent = parseFloat(item.tax_rate.rate) || 0;
      // Also check is_inclusive from tax_rate if available
      if (item.tax_rate.is_inclusive !== undefined) {
        isTaxInclusive = item.tax_rate.is_inclusive;
      }
      
      console.log(`[getPurchaseItemPricingDetails] Tax info found:`, {
        taxRateId,
        taxRatePercent,
        isTaxInclusive,
        components: item.tax_rate.components
      });
      
      // Get tax components if available
      if (item.tax_rate.components && Array.isArray(item.tax_rate.components)) {
        taxBreakdown = item.tax_rate.components.map((comp: any) => ({
          code: comp.code || '',
          rate: comp.rate || 0,
          amount: 0 // Will be calculated below
        }));
      }
    } else if (taxRateId) {
      // If we have tax_rate_id but no tax_rate object, try to fetch it
      console.log(`[getPurchaseItemPricingDetails] Tax rate ID ${taxRateId} found but no tax_rate object, fetching...`);
      
      const { getTaxRateAsync } = await import('@/lib/indexeddb/database');
      const taxRate = await getTaxRateAsync(itemId);
      
      if (taxRate) {
        taxRatePercent = parseFloat(taxRate.rate) || 0;
        if (taxRate.is_inclusive !== undefined) {
          isTaxInclusive = taxRate.is_inclusive;
        }
        
        console.log(`[getPurchaseItemPricingDetails] Fetched tax rate:`, {
          rate: taxRatePercent,
          is_inclusive: isTaxInclusive,
          components: taxRate.components
        });
        
        if (taxRate.components && Array.isArray(taxRate.components)) {
          taxBreakdown = taxRate.components.map((comp: any) => ({
            code: comp.code || '',
            rate: comp.rate || 0,
            amount: 0
          }));
        }
      }
    } else {
      console.log(`[getPurchaseItemPricingDetails] No tax info found for item ${itemId}`);
    }
    
    // Calculate with the recalculateItemPrice utility to ensure consistency
    const calculatedAmounts = recalculateItemPrice(
      itemId,
      taxRateId,
      quantity,
      roundedRate,
      existingDiscountPercent,
      existingDiscountAmount,
      isTaxInclusive,
      taxRatePercent,
      rateDecimalPlaces
    );
    
    // Update tax breakdown with calculated amounts
    if (taxBreakdown.length > 0) {
      const taxableAmount = calculatedAmounts.taxableAmount;
      taxBreakdown = taxBreakdown.map((comp) => ({
        ...comp,
        amount: Math.round((taxableAmount * comp.rate / 100) * 100) / 100
      }));
    }
    
    const result = {
      item_id: itemId,
      item_name: item?.name || '',
      item_code: item?.code || '',
      description: item?.description || item?.name || '',
      rate: roundedRate,
      rate_decimal_places: rateDecimalPlaces,
      discount_percent: existingDiscountPercent,
      discount_amount: existingDiscountAmount,
      tax_rate_id: taxRateId,
      tax_rate_percent: taxRatePercent,
      is_tax_inclusive: isTaxInclusive,
      taxable_amount: calculatedAmounts.taxableAmount,
      tax_amount: calculatedAmounts.taxAmount,
      amount: calculatedAmounts.totalAmount,
      applied_sale_rule_id: null, // No sale rules for purchase invoices
      tax_breakdown: taxBreakdown
    };
    
    console.log(`[getPurchaseItemPricingDetails] Returning result with tax_rate_percent: ${result.tax_rate_percent}`);
    
    return result;
  } catch (error) {
    console.error(`[invoiceCalculations] Error getting purchase pricing details for item ${itemId}:`, error);
    return null;
  }
}

/**
 * Format rate for display in input field
 */
export function formatRateForInput(rate: number | string | undefined, decimalPlaces: number = 0, isEditing: boolean = false): string {
  if (rate === undefined || rate === null || rate === '') {
    return '';
  }
  
  // If editing, return the raw value without formatting
  // This allows natural editing behavior (backspace, delete, etc.)
  if (isEditing) {
    const rateStr = String(rate);
    // Still preserve partial inputs during editing
    if (rateStr === '-' || rateStr === '.' || rateStr.endsWith('.')) {
      return rateStr;
    }
    // For numbers, check if it has trailing zeros after decimal that we want to preserve
    // e.g., "100.0" or "100.00" should be kept as-is during editing
    if (rateStr.includes('.') && /\.\d*0+$/.test(rateStr)) {
      return rateStr;
    }
    // Return the string representation without formatting
    return rateStr;
  }
  
  // If it's a string that represents a partial input (like "1." or "-"), return as is
  const rateStr = String(rate);
  if (rateStr === '-' || rateStr.endsWith('.')) {
    return rateStr;
  }
  
  // Otherwise, format with proper decimal places when not editing
  return formatRateForDisplay(rate, decimalPlaces);
}

/**
 * Parse user input for rate field
 */
export function parseRateInput(input: string, decimalPlaces: number = 0): number | null {
  if (!input || input === '-' || input === '.') {
    return null;
  }
  
  // Validate input format
  if (!isValidRateInput(input, decimalPlaces)) {
    return null;
  }
  
  const parsed = parseFloat(input);
  if (isNaN(parsed)) {
    return null;
  }
  
  // Round to configured decimal places
  return roundRate(parsed, decimalPlaces).toNumber();
}

/**
 * Calculate row totals based on current values
 */
export function calculateRowTotals(row: InvoiceLineItem): CalculatedTotals {
  const itemId = typeof row.item_id === 'number' ? row.item_id : parseInt(String(row.item_id || 0));
  const taxRateId = typeof row.tax_rate_id === 'number' ? row.tax_rate_id : 
    (row.tax_rate_id ? parseInt(String(row.tax_rate_id)) : null);
  const quantity = typeof row.quantity === 'number' ? row.quantity : parseFloat(String(row.quantity || 0));
  const rate = typeof row.rate === 'number' ? row.rate : parseFloat(String(row.rate || 0));
  const discountPercent = typeof row.discount_percent === 'number' ? row.discount_percent : 
    parseFloat(String(row.discount_percent || 0));
  const discountAmount = typeof row.discount_amount === 'number' ? row.discount_amount : 
    parseFloat(String(row.discount_amount || 0));
  const rateDecimalPlaces = row.rate_decimal_places || 0;
  const taxRatePercent = row.tax_rate_percent || 0;
  const isTaxInclusive = row.is_tax_inclusive || false;
  
  // Use centralized calculation
  const result = recalculateItemPrice(
    itemId || undefined,
    taxRateId || undefined,
    quantity,
    rate,
    discountPercent,
    discountAmount,
    isTaxInclusive,
    taxRatePercent,
    rateDecimalPlaces
  );
  
  return {
    taxable_amount: result.taxableAmount,
    tax_amount: result.taxAmount,
    amount: result.totalAmount
  };
}

/**
 * Validate and format rate based on decimal places
 * Used when user finishes editing (on blur)
 */
export function validateAndFormatRate(input: string, decimalPlaces: number = 0): {
  isValid: boolean;
  formattedValue: string;
  numericValue: number | null;
} {
  // Allow empty
  if (!input) {
    return { isValid: true, formattedValue: '', numericValue: null };
  }
  
  // Parse the input
  const parsed = parseRateInput(input, decimalPlaces);
  
  if (parsed === null) {
    return { isValid: false, formattedValue: input, numericValue: null };
  }
  
  return {
    isValid: true,
    formattedValue: formatRateForInput(parsed, decimalPlaces),
    numericValue: parsed
  };
}